import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Truck, PackageCheck } from "lucide-react";
import { api, gbp } from "../lib/api";
import { GatedPrice } from "../components/GatedPrice";
import { Countdown } from "../components/Countdown";
import { SEO } from "../components/SEO";
import { useBasket } from "../context/BasketContext";
import { useAuth } from "../context/AuthContext";
import { NotifyButton } from "../components/NotifyButton";
import { Gallery } from "../components/Gallery";

export default function DealDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isMember } = useAuth();
  const { add } = useBasket();
  const [deal, setDeal] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/deals/${slug}`).then(({ data }) => setDeal(data)).catch(() => navigate("/deals"));
  }, [slug, navigate]);

  if (!deal) return <div className="max-w-7xl mx-auto px-8 py-32 font-display text-3xl text-white/30">LOADING LOT…</div>;

  const soldOut = deal.stock_remaining <= 0;

  const addToBasket = () => {
    if (!isMember) { navigate("/join"); return; }
    add(deal, qty);
    toast.success(`${qty}x lot added to basket`, { description: deal.title });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 pb-32 lg:pb-12">
      <SEO
        title={deal.title}
        description={deal.description}
        image={deal.images[0]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: deal.title,
          description: deal.description,
          image: deal.images,
          sku: deal.sku,
          itemCondition: "https://schema.org/UsedCondition",
          offers: {
            "@type": "Offer",
            priceCurrency: "GBP",
            price: deal.member_price,
            availability: soldOut ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
            url: `${process.env.REACT_APP_BACKEND_URL}/deal/${deal.slug}`,
          },
        }}
      />
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <Gallery images={deal.images} alt={deal.title} />
          <div className="mt-8 border border-[#27272A]">
            <div className="px-5 py-3 border-b border-[#27272A] text-xs uppercase tracking-widest text-white/40 flex justify-between">
              <span>Lot Manifest — Verified</span><span>{deal.units} units total</span>
            </div>
            <table className="w-full text-xs sm:text-sm" data-testid="deal-manifest-table">
              <thead>
                <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="px-5 py-3">Item</th><th className="px-5 py-3 text-right">Qty</th><th className="px-5 py-3 text-right">RRP/Unit</th>
                </tr>
              </thead>
              <tbody>
                {deal.manifest.map((m, i) => (
                  <tr key={i} className="border-b border-[#27272A] last:border-0">
                    <td className="px-5 py-3">{m.item}</td>
                    <td className="px-5 py-3 text-right text-[#FFCC00]">{m.qty}</td>
                    <td className="px-5 py-3 text-right text-white/60">{gbp(m.rrp_each)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3 flex gap-4">
            <span>{deal.sku}</span>
            <span className="text-[#FFCC00]">{deal.condition}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-[0.95]" data-testid="deal-detail-title">{deal.title}</h1>
          <p className="text-white/50 text-sm leading-relaxed mt-6">{deal.description}</p>

          <div className="border border-[#27272A] p-6 mt-8">
            <GatedPrice deal={deal} size="lg" />
            {deal.status === "live" && !soldOut && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#27272A]">
                <div>
                  <div className="text-[10px] uppercase text-white/40 mb-1">Deal ends in</div>
                  <Countdown endsAt={deal.ends_at} size="lg" />
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-white/40 mb-1">Stock</div>
                  <div className="font-display text-3xl text-[#FF3B30]" data-testid="deal-stock-remaining">{deal.stock_remaining} LOTS</div>
                </div>
              </div>
            )}
          </div>

          {soldOut ? (
            <div className="mt-6 border border-[#FF3B30] p-6 text-center font-display text-3xl text-[#FF3B30]">GONE. IT'S FINISHED.</div>
          ) : deal.status === "coming_soon" ? (
            <div className="mt-6 border border-[#27272A] p-6">
              <div className="text-center font-display text-2xl text-white/50 mb-4">DROPPING SOON — NOT YET LIVE</div>
              <NotifyButton deal={deal} size="lg" />
              <p className="text-white/30 text-[11px] mt-3 text-center">We'll email you the second this deal goes live.</p>
            </div>
          ) : (
            <div className="mt-6 flex gap-4">
              <div className="flex border border-[#27272A]">
                <button data-testid="qty-decrease-button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 hover:bg-[#1A1A1A]"><Minus size={16} /></button>
                <span data-testid="qty-value" className="px-6 py-4 font-display text-xl border-x border-[#27272A]">{qty}</span>
                <button data-testid="qty-increase-button" onClick={() => setQty(Math.min(deal.stock_remaining, qty + 1))} className="px-4 hover:bg-[#1A1A1A]"><Plus size={16} /></button>
              </div>
              <button data-testid="add-to-basket-button" onClick={addToBasket} className="btn-industrial flex-1 py-4 text-xl">
                {isMember ? "ADD TO BASKET" : "JOIN FREE TO BUY"}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-px bg-[#27272A] border border-[#27272A] mt-8 text-xs">
            <div className="bg-[#0A0A0A] p-5 flex gap-3 items-start"><Truck size={16} className="text-[#FFCC00] shrink-0" /><div><div className="text-white">UK Pallet Delivery</div><div className="text-white/40 mt-1">Zone-priced from £29, 3–5 working days</div></div></div>
            <div className="bg-[#0A0A0A] p-5 flex gap-3 items-start"><PackageCheck size={16} className="text-[#FFCC00] shrink-0" /><div><div className="text-white">Free Collection</div><div className="text-white/40 mt-1">From our Midlands warehouse</div></div></div>
          </div>
        </div>
      </div>

      {/* Sticky mobile buy bar */}
      {deal.status === "live" && !soldOut && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-[#0A0A0A] border-t border-[#27272A] p-4 flex items-center gap-4 z-40">
          <div className={`font-display text-2xl text-[#FFCC00] ${isMember ? "" : "price-locked"}`}>{isMember ? gbp(deal.member_price) : "£888"}</div>
          <button data-testid="mobile-buy-bar-button" onClick={addToBasket} className="btn-industrial flex-1 py-3 text-lg">
            {isMember ? "ADD TO BASKET" : "JOIN TO BUY"}
          </button>
        </div>
      )}
    </div>
  );
}
